import { DISMISS, Formik, useNotification } from "collections";
import React, { useCallback } from "react";
import {
  TestTeamsWebhookByIdMutationVariables,
  useTestTeamsWebhookByIdMutation
} from "generated/graphql";
import * as yup from "yup";
import { Form } from "collections/Form/Form";
import { SubmitButton } from "elements";

const TestFormSchema = yup.object({
  testActionType: yup
    .string()
    .oneOf(["DEVICE_CAN_BE_UPDATED"])
    .required()
});

/**
 * Provide a shared submit action handler for forms to test a webhook.
 * You can call this on its own, or provide it to a form's onSubmit to also track
 * the submission process.
 */
export const useTestFormBehavior = () => {
  const [addNotification] = useNotification();
  const [, test] = useTestTeamsWebhookByIdMutation();
  return useCallback(
    async (params: TestTeamsWebhookByIdMutationVariables) => {
      const { data, error } = await test(params, {
        requestPolicy: "network-only"
      });
      if (error) return; //Notification of an invocation error is handled by the exchange
      if (data?.ncTestTeamsWebhook.success === true) {
        addNotification({
          type: "success",
          message: data.ncTestTeamsWebhook.message ?? "",
          dismiss: DISMISS.DEFAULT
        });
      }
      if (data?.ncTestTeamsWebhook.success === false) {
        addNotification({
          type: "error",
          message: data.ncTestTeamsWebhook.message ?? "",
          dismiss: DISMISS.DEFAULT
        });
      }
    },
    // omit t and addNotification from deps, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [test]
  );
};

export const TestFormLinkStyle: React.FC<{
  webHooksId: string;
}> = ({ webHooksId }) => {
  const onSubmit = useTestFormBehavior();
  return (
    <Formik
      name="TestWebhookForm"
      validationSchema={TestFormSchema}
      initialValues={{
        testActionType: "DEVICE_CAN_BE_UPDATED"
      }}
      onSubmit={() => onSubmit({ id: webHooksId })}
    >
      {({ isSubmitting }) => (
        <Form>
          <SubmitButton
            isSubmitting={isSubmitting}
            className="btn btn-link"
            labelKey="TEST_WEBHOOK"
            submittingLabelKey="TESTING"
          />
        </Form>
      )}
    </Formik>
  );
};

export const TestForm: React.FC<{
  webHooksId: string;
}> = ({ webHooksId }) => {
  const onSubmit = useTestFormBehavior();
  return (
    <Formik
      name="TestWebhookForm"
      validationSchema={TestFormSchema}
      initialValues={{
        testActionType: "DEVICE_CAN_BE_UPDATED"
      }}
      onSubmit={() => onSubmit({ id: webHooksId })}
    >
      {({ isSubmitting }) => (
        <Form style={{ display: "inline-block", marginLeft: 8 }}>
          <SubmitButton
            isSubmitting={isSubmitting}
            labelKey="TEST_WEBHOOK"
            submittingLabelKey="TESTING"
          />
        </Form>
      )}
    </Formik>
  );
};
